import { Arr, NullFunction } from '@deqo/foundation';
import React, { ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSessionStorage } from 'react-use';
import styled, { css } from 'styled-components';
import { Icon } from '../../../concepts/resources/Icon';
import { Colors, toRem } from '../../../ui';
import { PopupTabs } from '../../../ui/PopupTabs';
import { FlexScroller, ScrollerInteral, ScrollerValues } from '../../../ui/Scrollers';
import { Tip } from '../../../ui/Tip';
import { Flexer, Title, TitleLine } from '../../dashboard/elements/LayoutElements';
import { DragOrClick, DraggableClickable } from './DraggableClickable';

const ElementSelectorContext = React.createContext({
    name: '',
    root: null as HTMLElement | null,
    activeTab: '',
    setActiveTab: NullFunction as (name: string) => void,
});

const Menu = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
    gap: .125rem;
    padding: .5rem .5rem 0rem;
    border-bottom: solid 1px #F0F2F5;
`;

type MenuItemProps = {
    anchor: string,
    label: string,
    icon: string,
    iconSize?: string | number,
    onSelect?: () => void,
};

const MenuItemFrame = styled.div<{ selected?: boolean }>`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 0 .5rem .5rem;
    color: #546077;
    cursor: pointer;
    font-weight: 400;
    user-select: none;

    &:after {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: .25rem;
        background: none;
    }

    &:hover {
        &:after { background: ${Colors.primaryLight}; }
        color: ${Colors.primaryLight};
    }
    &:active {
        &:after { background: ${Colors.primary}; }
        color: ${Colors.primaryText};
    }
    ${p => !p.selected ? '' : css`
        &:after { background: ${Colors.primary}; }
        color: ${Colors.primaryText};
    `}
`;

const MenuItem = ({ anchor, label, icon, iconSize = '2', onSelect }: MenuItemProps) => {

    const { activeTab, setActiveTab } = useContext(ElementSelectorContext);
    const selected = activeTab === anchor;
    const onClick = () => {
        setActiveTab(anchor);
        onSelect?.();
    };

    return <Tip text={label} position="top">
        <MenuItemFrame data-anchor={anchor} {...{ selected, onClick }} >
            <Icon size={iconSize} src={icon} />
        </MenuItemFrame>
    </Tip>;
};

const getAnchorForScrollerValues = (root: HTMLElement, values: ScrollerValues) => {
    const anchors = Array.from(root.querySelectorAll('[data-content] [data-anchor]')) as HTMLElement[];
    if (values.top === 0) return Arr.first(anchors);
    if (values.top === 1) return Arr.last(anchors);

    const includeLine = values.scrollTop + (values.clientHeight * .25);
    const elmts = anchors.map(a => a).filter(x => x.offsetTop <= includeLine);
    const dist = (x: HTMLElement) => Math.abs(x.offsetTop - includeLine);

    const [hit] = elmts.sort((a, b) => dist(a) - dist(b));
    return hit ?? null;
};

const getScrollPositionForAnchor = (root: HTMLElement, scroller: HTMLElement, name: string) => {
    const all = Array.from(root.querySelectorAll('[data-content] [data-anchor]')) as HTMLElement[];
    const target = all.find(x => x.dataset.anchor === name);
    if (!target) return 0;
    const y = target.offsetTop - (all.indexOf(target) === all.length - 1 ? 0 : 1);
    return y;
};

const Content = ({ children }: { children?: ReactNode }) => {

    const { root, activeTab, setActiveTab } = useContext(ElementSelectorContext);
    const scroller = useRef<ScrollerInteral>();

    const [initialScroll, setInitialScroll] = useSessionStorage(`deqo/ui/elementSelector/${name}`, 0);

    const onScroll = useCallback((values: ScrollerValues) => {
        const value = getAnchorForScrollerValues(root, values)?.dataset?.anchor;
        if (value !== activeTab) setActiveTab(value);
        setInitialScroll(values.scrollTop);
    }, [root, activeTab]);

    useEffect(() => {
        if (!scroller.current) return;
        const scrollerElmt = scroller.current.getScrollElement();
        const expected = getAnchorForScrollerValues(root, ScrollerValues.for(scrollerElmt))?.dataset?.anchor;
        if (expected !== activeTab && !!activeTab) {
            const y = getScrollPositionForAnchor(root, scrollerElmt, activeTab);
            scrollerElmt.scrollTo({ top: y });
        }
    }, [activeTab]);

    useEffect(() => {
        if (!scroller.current) return;
        scroller.current.getScrollElement().scrollTo({ top: initialScroll });
    }, [scroller.current]);

    return <Flexer data-content flex="column" grow padding=".125">
        <FlexScroller internalRef={scroller} autoHide={false} onScroll={onScroll}>
            <Flexer padding=".125 .75 .125 .375">
                {children}
            </Flexer>
        </FlexScroller>
    </Flexer>;
};

type GroupProps = {
    label: string,
    anchor: string,
    children?: ReactNode,
};

const Group = ({ label, anchor, children }: GroupProps) => {
    return <Flexer data-anchor={anchor} padding=".25 0">
        <TitleLine>
            <Title size="xs" color={Colors.textLight} weight={500}>{label}</Title>
        </TitleLine>
        {children}
    </Flexer>;
};

const ItemFrame = styled.div<{ selected?: boolean }>`
    position: relative;
    display: flex;
    flex-direction: column;
    position: relative;
    flex: 1 1 auto;
    /* min-height: 8rem;
    min-width: 9rem; */
    color: ${Colors.stone400};

    &:hover {
        background: ${Colors.hover};
    }

    &:active {
        background: ${Colors.active};
    }

    ${p => p.selected && css`
        &:after {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            border: solid .125rem ${Colors.primary};
            border-radius: .25rem;
        }
    `}
`;

const ItemLabel = styled.div`
    margin-left: .25rem;
    margin-bottom: .25rem;
    flex: 0 0 auto;
    font-size: .75rem;
`;

type ItemProps = {
    label?: string,
    selected?: boolean,
    onAction(type: DragOrClick): void,
    children?: ReactNode,
};

const Item = ({ label, selected, onAction, children }: ItemProps) =>
    <DraggableClickable onAction={onAction}>
        <ItemFrame {...{ selected }}>
            {label && <ItemLabel>{label}</ItemLabel>}
            {children}
        </ItemFrame>
    </DraggableClickable>;

const Footer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    border-top: solid 1px ${Colors.border};
    padding: .5rem .5rem;
`;

type ElementSelectorProps = {
    name: string,
    contentWidth: string | number,
    height: string | number,
    children?: ReactNode,
};

const BaseFrame = styled.div<{ width: string | number, height: string | number }>`
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    width: ${p => toRem(p.width)};
    height: ${p => toRem(p.height)};

    ${PopupTabs} {
        margin-bottom: 0;
    }

    ${PopupTabs.Item} {
        width: 3.15rem;
        justify-content: center;
    }
`;

const Base = ({ name, children, contentWidth, height }: ElementSelectorProps) => {
    const [root, setRoot] = useState<HTMLElement | null>(null);
    const [activeTab, setActiveTab] = useState('');
    return <ElementSelectorContext.Provider value={{ name, root, activeTab, setActiveTab }}>
        <BaseFrame ref={setRoot} width={parseFloat(`${contentWidth}`) + 1} height={height}>
            {children}
        </BaseFrame>
    </ElementSelectorContext.Provider>;
};

export const ElementSelector2 = Object.assign(Base, {
    Menu,
    MenuItem,
    Content,
    Group,
    Item,
    Footer,
});
